/*
 * Copyright © HatioLab Inc. All rights reserved.
 *
 * Tooltip3D — 3D-anchored DOM popup with curved tether line.
 *
 * First-class HUD-popup component. Relies entirely on things-scene's
 * `state.screenFixed` HUD pipeline:
 *   - `screenFixed=true` is asserted in `ready()` so legacy boards lacking
 *     the flag still resolve to HUD mode
 *   - framework's `addObject` routes screenFixed DOM components into
 *     `hudCSS3DScene` (rendered by `hudCSS3DRenderer`, orthographic
 *     HUD camera). Re-add path (`three-capability.ts:498-525`) preserves
 *     the same routing on subsequent dimension/property changes
 *   - `MixinHTMLElement.reposition` is a no-op for screenFixed → our theme
 *     CSS isn't wiped by the 2D-canvas-overlay reposition path
 *   - `RealObjectDomElement.build/updateDimension` skip the
 *     `width/height = bounds.{w,h}px` enforcement for screenFixed → panel
 *     stays content-driven sized (not clamped to the 16x16 marker bounds)
 *
 * Tooltip3DReal subclass overrides `updateTransform` only — to project
 * the 3D anchor through the active camera into HUD frame coords. The
 * connector SVG is drawn into `layer.target` (always visible, canvas-pixel
 * space) with mathematical projection so endpoints align with the HUD
 * panel even though they're in different DOM trees.
 */

import {
  Component,
  ComponentNature,
  HTMLOverlayContainer,
  RealObject,
  RealObjectDomElement,
  sceneComponent,
  resolveAnchorWorldPosition,
  type AnchorPin,
  buildTether,
  updateTether,
  clearTether,
  disposeTether,
  type TetherElements
} from '@hatiolab/things-scene'
import type { State } from '@hatiolab/things-scene'
import {
  Placeable,
  type Alignment,
  type Heights,
  type PlacementArchetype
} from '@operato/scene-base'
import * as THREE from 'three'

type ThemeKey = 'minimal' | 'card' | 'sci-fi'

/** Tooltip3D 컴포넌트 state */
export interface Tooltip3DState extends State {
  anchorRef?: string
  anchorOffsetX?: number
  anchorOffsetY?: number
  anchorOffsetZ?: number
  theme?: ThemeKey
  anchorPin?: AnchorPin
  screenFixed?: boolean
  alpha?: number
}

const NATURE: ComponentNature = {
  mutable: false,
  resizable: true,
  rotatable: false,
  properties: [
    {
      type: 'string',
      label: 'anchor-ref',
      name: 'anchorRef',
      placeholder: 'component id'
    },
    { type: 'number', label: 'anchor-offset-x', name: 'anchorOffsetX' },
    { type: 'number', label: 'anchor-offset-y', name: 'anchorOffsetY' },
    { type: 'number', label: 'anchor-offset-z', name: 'anchorOffsetZ' },
    {
      type: 'select',
      label: 'theme',
      name: 'theme',
      property: {
        options: [
          { display: 'Minimal', value: 'minimal' },
          { display: 'Card', value: 'card' },
          { display: 'Sci-Fi', value: 'sci-fi' }
        ]
      }
    },
    {
      type: 'select',
      label: 'anchor-pin',
      name: 'anchorPin',
      property: {
        options: [
          { display: 'Top', value: 'top' },
          { display: 'Center', value: 'center' },
          { display: 'Bottom', value: 'bottom' }
        ]
      }
    }
  ],
  help: 'scene/component/tooltip-3d'
}

const THEME_STYLES: Record<ThemeKey, Partial<CSSStyleDeclaration>> = {
  minimal: {
    background: 'rgba(20, 24, 32, 0.92)',
    color: '#f0f4ff',
    padding: '8px 12px',
    borderRadius: '6px',
    font: '12px -apple-system, system-ui, sans-serif',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
    maxWidth: '280px',
    boxSizing: 'border-box'
  },
  card: {
    background: 'linear-gradient(135deg, rgba(40, 50, 70, 0.95), rgba(20, 28, 44, 0.95))',
    color: '#ffffff',
    padding: '14px 18px',
    borderRadius: '12px',
    font: '13px -apple-system, system-ui, sans-serif',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(8px)',
    maxWidth: '320px',
    boxSizing: 'border-box'
  },
  'sci-fi': {
    background: 'rgba(10, 25, 40, 0.85)',
    color: '#7af9ff',
    padding: '10px 14px',
    borderRadius: '2px',
    font: '12px "SF Mono", Menlo, monospace',
    border: '1px solid #4ad8e0',
    boxShadow: '0 0 12px rgba(74, 216, 224, 0.4), inset 0 0 8px rgba(74, 216, 224, 0.15)',
    textShadow: '0 0 4px rgba(122, 249, 255, 0.6)',
    maxWidth: '300px',
    letterSpacing: '0.5px',
    boxSizing: 'border-box'
  }
}

const CONNECTOR_COLORS: Record<ThemeKey, string> = {
  minimal: 'rgba(240, 244, 255, 0.5)',
  card: 'rgba(255, 255, 255, 0.45)',
  'sci-fi': 'rgba(122, 249, 255, 0.85)'
}

@sceneComponent('tooltip-3d')
export class Tooltip3D extends Placeable(HTMLOverlayContainer) {
  declare state: Tooltip3DState

  static placement: PlacementArchetype = 'signage'
  static align: Alignment = 'bottom'
  static defaultDepth = (h: Heights) => Math.max(20, (h.ceiling - h.signage) * 0.3)

  get nature() {
    return NATURE
  }

  get anchors() {
    return []
  }

  get tagName() {
    return 'div'
  }

  get hasTextProperty() {
    return true
  }

  setElementProperties(element: HTMLElement) {
    Object.assign(element.style, THEME_STYLES[this._themeKey()])
    const state = this.state
    if (typeof state.fillStyle === 'string' && state.fillStyle && state.fillStyle !== 'transparent') {
      element.style.background = state.fillStyle
    }
    if (typeof state.fontColor === 'string' && state.fontColor) {
      element.style.color = state.fontColor
    }
    if (typeof state.fontFamily === 'string' && state.fontFamily) {
      element.style.fontFamily = state.fontFamily
    }
    if (typeof state.fontSize === 'number' && state.fontSize > 0) {
      element.style.fontSize = state.fontSize + 'px'
    }
    element.style.fontWeight = state.bold ? 'bold' : ''
    element.style.fontStyle = state.italic ? 'italic' : ''
    if (typeof state.textAlign === 'string' && state.textAlign) {
      element.style.textAlign = state.textAlign as any
    }
    if (
      typeof state.strokeStyle === 'string' &&
      state.strokeStyle &&
      typeof state.lineWidth === 'number' &&
      state.lineWidth > 0
    ) {
      element.style.border = `${state.lineWidth}px solid ${state.strokeStyle}`
    } else {
      element.style.border = ''
    }
    element.style.pointerEvents = 'auto'
    element.style.width = 'auto'
    element.style.height = 'auto'
    element.innerHTML = this.text || ''

    /* Tooltip3D 는 HUD CSS3D 모드 전용 (3D 앵커 추적). 2D 모드에서는 anchor 가
     * 의미 없고, element 가 그냥 overlay 에 떠있어 시각적으로 거슬림. 3D 모드에서만
     * Tooltip3DReal.build 가 display='' 로 다시 켬. */
    const is3dMode = this.rootModel?.is3dMode
    element.style.display = is3dMode ? '' : 'none'
  }

  private _themeKey(): ThemeKey {
    const t = this.getState('theme') as ThemeKey | undefined
    return t && t in CONNECTOR_COLORS ? t : 'minimal'
  }

  buildRealObject(): RealObject | undefined {
    return new Tooltip3DReal(this)
  }

  /**
   * Tooltip3D is by definition a HUD popup. Older saved boards may lack
   * `state.screenFixed` (template-only fields don't backfill). Asserting
   * before `super.ready()` guarantees framework HUD-aware paths
   * (addObject routing, reposition skip, build/updateDimension skip)
   * see screenFixed=true from the very first lifecycle tick.
   */
  async ready() {
    if (!this.state.screenFixed) {
      this.set({ screenFixed: true } as any)
    }
    await super.ready()
  }
}

/**
 * RealObject for Tooltip3D. Inherits all framework HUD-popup behavior
 * (CSS3D wrapping, hudCSS3DScene placement, content-driven sizing) from
 * `RealObjectDomElement`. The only Tooltip3D-specific work:
 *
 *   - `build`: register redraw listener + create connector SVG
 *   - `updateTransform`: project anchor to HUD frame each redraw, place
 *     panel CSS3DObject, draw the connector
 *   - `dispose`: clean up redraw subscription + SVG
 */
class Tooltip3DReal extends RealObjectDomElement {
  private _redrawHandler?: () => void
  private _tether?: TetherElements

  build() {
    super.build()
    /* 3D 모드 진입 — element 가 hudDiv 로 reparent 되며 가시화. 2D 에서 hide 해둔
     * display 를 다시 켜야 함. */
    const el = (this.component as any).element as HTMLElement | undefined
    if (el) el.style.display = ''
    this._buildTether()
    this._hookRedraw()
  }

  /**
   * Subscribe once. `RealObject.updateDimension` triggers `clear() + build()`
   * for non-screenFixed paths — defensive against any future code path that
   * could re-invoke build() and would otherwise stack redraw handlers.
   */
  private _hookRedraw() {
    if (this._redrawHandler) return
    const layer: any = (this.component as any).rootModel ?? (this.component as any).root
    if (!layer?.on) return
    this._redrawHandler = () => this.updateTransform()
    layer.on('redraw', this._redrawHandler)
    layer.invalidate?.()
  }

  /**
   * Project 3D anchor to screen, position panel in HUD frame, draw the
   * connector in canvas pixel space.
   *
   * Two coord spaces, kept distinct:
   *   - **HUD model pixels** for placing the panel CSS3DObject. HUD
   *     camera frustum is fixed at `[-modelW/2, modelW/2]` × `[modelH/2,
   *     -modelH/2]`; setting object3d.position to (hudX, hudY, 0)
   *     renders panel CENTER at hudDiv intrinsic pixel `(modelW/2 +
   *     hudX, modelH/2 - hudY)`. hudDiv is then CSS-scaled by `(canvasW
   *     /modelW, canvasH/modelH)` to fit the visible canvas
   *     (`renderer-manager.ts:317-323`).
   *   - **Canvas pixels** for the SVG connector. SVG lives in
   *     `layer.target` (always visible) with viewBox sized to canvas.
   *     Panel center in canvas pixels = `(cx * canvasW/modelW, cy *
   *     canvasH/modelH)`; panel rendered size in canvas pixels =
   *     `(panelW * scaleX, panelH * scaleY)`.
   */
  updateTransform() {
    if (this.suppressTransform) return

    const panel = (this.component as any).element as HTMLElement | undefined
    if (!panel) return

    const camera = this._resolveCamera()
    const anchor = this._resolveAnchorWorld()
    const dims = this._dims()

    if (!camera || !anchor || !dims) {
      panel.style.opacity = '0'
      if (this._tether) clearTether(this._tether)
      return
    }

    const { canvasW, canvasH, modelW, modelH } = dims
    const scaleX = canvasW / modelW
    const scaleY = canvasH / modelH

    /* 앵커 world 좌표 → 카메라 NDC. NDC 기반 frustum 검사 후 모델 픽셀 변환. */
    camera.updateMatrixWorld(true)
    const ndc = anchor.clone().project(camera)
    if (
      !Number.isFinite(ndc.x) ||
      !Number.isFinite(ndc.y) ||
      ndc.x < -1.5 ||
      ndc.x > 1.5 ||
      ndc.y < -1.5 ||
      ndc.y > 1.5
    ) {
      panel.style.opacity = '0'
      if (this._tether) clearTether(this._tether)
      return
    }

    const ax = ((ndc.x + 1) / 2) * modelW
    const ay = ((1 - ndc.y) / 2) * modelH

    const panelW = panel.offsetWidth || 200
    const panelH = panel.offsetHeight || 60

    let cx = ax + 24 + panelW / 2
    let cy = ay - 24 - panelH / 2
    if (cx + panelW / 2 > modelW - 8) cx = ax - 24 - panelW / 2
    if (cy - panelH / 2 < 8) cy = ay + 24 + panelH / 2
    cx = Math.max(panelW / 2 + 8, Math.min(cx, modelW - panelW / 2 - 8))
    cy = Math.max(panelH / 2 + 8, Math.min(cy, modelH - panelH / 2 - 8))

    const hudX = cx - modelW / 2
    const hudY = -(cy - modelH / 2)
    this.object3d.position.set(hudX, hudY, 0)
    this.object3d.rotation.set(0, 0, 0)

    const alphaState = (this.component.state as any).alpha
    const alpha = typeof alphaState === 'number' && Number.isFinite(alphaState) ? alphaState : 1
    panel.style.opacity = String(alpha)

    if (this._tether) {
      updateTether(
        this._tether,
        ax * scaleX,
        ay * scaleY,
        (cx - panelW / 2) * scaleX,
        (cy - panelH / 2) * scaleY,
        panelW * scaleX,
        panelH * scaleY,
        CONNECTOR_COLORS[this._themeKey()],
        alpha,
        canvasW,
        canvasH
      )
    }
  }

  private _dims():
    | { canvasW: number; canvasH: number; modelW: number; modelH: number }
    | undefined {
    const layer: any = (this.component as any).rootModel ?? (this.component as any).root
    if (!layer) return undefined

    const bounds = layer.bounds
    const modelW = Math.abs(bounds?.width || 0) || 1
    const modelH = Math.abs(bounds?.height || 0) || 1

    let canvasW = modelW
    let canvasH = modelH
    const target = layer.target as HTMLElement | undefined
    if (target) {
      const rect = target.getBoundingClientRect()
      if (rect.width > 0) canvasW = rect.width
      if (rect.height > 0) canvasH = rect.height
    }
    if (canvasW <= 0 || canvasH <= 0) return undefined
    return { canvasW, canvasH, modelW, modelH }
  }

  private _resolveCamera(): THREE.Camera | undefined {
    let node: any = this.component
    while (node) {
      if (node.activeCamera3d) return node.activeCamera3d
      node = node.parent
    }
    return undefined
  }

  /**
   * 앵커 월드 좌표 해석 — 공통 유틸 `resolveAnchorWorldPosition` 위임.
   * state.anchorRef 가 있으면 그 컴포넌트, 없으면 자기 자신을 target 으로.
   * pin / offset 도 state 에서 추출.
   */
  private _resolveAnchorWorld(): THREE.Vector3 | null {
    const state = this.component.state as any
    const refId = state.anchorRef as string | undefined

    let target: Component = this.component as any
    if (refId) {
      const root: any = (this.component as any).rootModel ?? (this.component as any).root
      const refTarget = root?.findById?.(refId)
      if (refTarget) target = refTarget
    }

    return resolveAnchorWorldPosition(target, (state.anchorPin as AnchorPin) || 'top', {
      x: state.anchorOffsetX ?? 0,
      y: state.anchorOffsetY ?? 0,
      z: state.anchorOffsetZ ?? 0
    })
  }

  /**
   * Connector tether — 공통 유틸 `buildTether` 위임. host 는 `layer.target` (always
   * visible canvas-pixel overlay). hudDiv 와 다른 DOM tree 지만, `updateTransform`
   * 의 model→canvas 픽셀 변환으로 endpoint 가 패널과 픽셀-퍼펙트 맞춰짐.
   */
  private _buildTether() {
    if (this._tether) return
    const layer: any = (this.component as any).rootModel ?? (this.component as any).root
    const host = layer?.target as HTMLElement | undefined
    if (!(host instanceof HTMLElement)) return
    this._tether = buildTether(host)
  }

  private _themeKey(): ThemeKey {
    const t = this.component.getState('theme') as ThemeKey | undefined
    return t && t in CONNECTOR_COLORS ? t : 'minimal'
  }

  dispose() {
    if (this._redrawHandler) {
      const layer: any = (this.component as any).rootModel ?? (this.component as any).root
      layer?.off?.('redraw', this._redrawHandler)
      this._redrawHandler = undefined
    }
    if (this._tether) {
      disposeTether(this._tether)
      this._tether = undefined
    }
    /* 3D 비활성화 / dispose — element 를 다시 hide. 3D 재활성화 시 build 가 보임. */
    const el = (this.component as any).element as HTMLElement | undefined
    if (el) el.style.display = 'none'
    super.dispose()
  }
}
